import { JobRating } from "./JobOrder";
import { User } from "./User";
import { Vessel } from "./Vessel";

export type MarinerProfile = {
  id?: number;
  firstName: string;
  middleName: string | undefined;
  lastName: string;
  mmcId: string;
  dateOfBirth: string; // string type to bind directly to date input
  gender: Gender | undefined;
  rating: JobRating | undefined;
  type: MarinerType;
  status: MarinerStatus;
  agentUnion: string | undefined;
  twicTsaNum: string;
  frequentFlyerNumber?: string;
  phone: string | undefined;
  email: string | undefined;
  placeOfBirth: string | undefined;
  seniority?: string | undefined;
  user?: User | undefined;
  cognitoUser?: User | undefined;
  veteran: boolean;
  trainingStatus: TrainingStatus | undefined;
  vessel: Vessel | undefined;
  captainView?: boolean;
};

export enum Gender {
  MALE = "MALE",
  FEMALE = "FEMALE",
}

export const GenderLabel: { [key in Gender]: string } = {
  [Gender.MALE]: "Male",
  [Gender.FEMALE]: "Female",
};

export const GenderSymbol: { [key in Gender]: string } = {
  [Gender.MALE]: "M",
  [Gender.FEMALE]: "F",
};

export enum MarinerType {
  LICENSED = "LICENSED",
  UNLICENSED = "UNLICENSED",
}

export enum MarinerStatus {
  ON_BOARD = "ON_BOARD",
  ACTIVE = "ACTIVE",
  PENDING = "PENDING",
  UNAVAILABLE = "UNAVAILABLE",
  INACTIVE = "INACTIVE",
}

export const MarinerStatusLabel: { [key in MarinerStatus]: string } = {
  [MarinerStatus.ON_BOARD]: "On board",
  [MarinerStatus.ACTIVE]: "Active",
  [MarinerStatus.PENDING]: "Pending",
  [MarinerStatus.UNAVAILABLE]: "Unavailable",
  [MarinerStatus.INACTIVE]: "Inactive",
};

export enum TrainingStatus {
  PERMANENT = "PERMANENT",
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export const TrainingStatusLabel: { [key in TrainingStatus]: string } = {
  [TrainingStatus.PERMANENT]: "Permanent",
  [TrainingStatus.ACTIVE]: "Active",
  [TrainingStatus.INACTIVE]: "Inactive",
}

export const RatingTypeMap = new Map<JobRating, MarinerType>([
  [JobRating._Master, MarinerType.LICENSED],
  [JobRating._CM, MarinerType.LICENSED],
  [JobRating._2M, MarinerType.LICENSED],
  [JobRating._3M, MarinerType.LICENSED],
  [JobRating._JM, MarinerType.LICENSED],
  [JobRating._CE, MarinerType.LICENSED],
  [JobRating._1AE, MarinerType.LICENSED],
  [JobRating._2AE, MarinerType.LICENSED],
  [JobRating._3AE, MarinerType.LICENSED],
  [JobRating._PTE, MarinerType.LICENSED],
  [JobRating._AE, MarinerType.LICENSED],
  [JobRating._PE, MarinerType.LICENSED],
  [JobRating._PIL, MarinerType.LICENSED],
  [JobRating._REO, MarinerType.LICENSED],
  [JobRating._ETO, MarinerType.LICENSED],
  [JobRating._PM, MarinerType.LICENSED],
  [JobRating._PRE, MarinerType.LICENSED],
  [JobRating._DECKCADET, MarinerType.LICENSED],
  [JobRating._ENGINECADET, MarinerType.LICENSED],
  [JobRating._TAPP, MarinerType.LICENSED],
  [JobRating._CHC, MarinerType.UNLICENSED],
  [JobRating._2CB, MarinerType.UNLICENSED],
  [JobRating._AC, MarinerType.UNLICENSED],
  [JobRating._BSN, MarinerType.UNLICENSED],
  [JobRating._GUDE, MarinerType.UNLICENSED],
  [JobRating._GUDEM1D, MarinerType.UNLICENSED],
  [JobRating._GUDEM1E, MarinerType.UNLICENSED],
  [JobRating._MDR, MarinerType.UNLICENSED],
  [JobRating._OIL, MarinerType.UNLICENSED],
  [JobRating._OMU, MarinerType.UNLICENSED],
  [JobRating._ABB, MarinerType.UNLICENSED],
  [JobRating._ABM, MarinerType.UNLICENSED],
  [JobRating._ABG, MarinerType.UNLICENSED],
  [JobRating._ABS, MarinerType.UNLICENSED],
  [JobRating._ABT, MarinerType.UNLICENSED],
  [JobRating._AB, MarinerType.UNLICENSED],
  [JobRating._OS, MarinerType.UNLICENSED],
  [JobRating._QMED, MarinerType.UNLICENSED],
  [JobRating._QMEDDAY, MarinerType.UNLICENSED],
  [JobRating._QMEDE, MarinerType.UNLICENSED],
  [JobRating._SA, MarinerType.UNLICENSED],
  [JobRating._STB, MarinerType.UNLICENSED],
  [JobRating._STW, MarinerType.UNLICENSED],
  [JobRating._STKP, MarinerType.UNLICENSED],
  [JobRating._UNLICENSEDAPPRENTICE, MarinerType.UNLICENSED],
  [JobRating._WPR, MarinerType.UNLICENSED],
  [JobRating._STOS, MarinerType.UNLICENSED],
  [JobRating._OL, MarinerType.UNLICENSED],
  [JobRating._GVA, MarinerType.UNLICENSED],
  [JobRating._CC, MarinerType.UNLICENSED],
  [JobRating._2CC, MarinerType.UNLICENSED],
  [JobRating._SBY, MarinerType.UNLICENSED],
  [JobRating._GU, MarinerType.UNLICENSED],
  [JobRating._QEE, MarinerType.UNLICENSED],
  [JobRating._OSA2, MarinerType.UNLICENSED],
  [JobRating._OSA3, MarinerType.UNLICENSED],
  [JobRating._WA2, MarinerType.UNLICENSED],
  [JobRating._WA3, MarinerType.UNLICENSED],
]);

export enum MarinerUnion {
  AMO = "AMO",
  SIU = "SIU",
  NON_UNION = "NON-UNION",
}

export type marinerSearchParams = {
  search: string;
  ratings: string[];
  vesselId: number | undefined;
  registered: boolean | undefined;
  compatibleWithRating: string;
  smis: string;
  veteran: boolean | undefined;
  trainingStatus: string;
  page: number;
  size: number;
  sortColumn: string;
  sortDirection: "asc" | "desc" | undefined;
};
